import { z } from 'zod'
import { Address, DecimalString, Hash32, IsoTimestamp, WeiString } from '../primitives.js'
import { UnsignedTx } from './tokens.js'

/**
 * In-app trading of agent tokens by holders from their own wallet (docs/API.md "Token trading"): quotes
 * from the Uniswap V4Quoter, swaps through the UniversalRouter (`V4_SWAP`, `SWAP_EXACT_IN_SINGLE`).
 * `buy` spends the pool's quote asset, `sell` spends agent tokens. Amounts are base units of the input.
 */
export const TokenTradeSide = z.enum(['buy', 'sell'])
export type TokenTradeSide = z.output<typeof TokenTradeSide>

const SlippageBps = z.coerce.number().int().min(0).max(5000)

export const TokenQuoteQuery = z.object({
  side: TokenTradeSide,
  /** Exact input in base units (quote asset for `buy`, agent token for `sell`). */
  amount: WeiString.refine((v) => v !== '0', 'amount must be > 0'),
  slippageBps: SlippageBps.default(100),
})
export type TokenQuoteQuery = z.input<typeof TokenQuoteQuery>

export const TokenTradeQuote = z.object({
  token: Address,
  side: TokenTradeSide,
  /** Pool quote asset; zero address = native ETH. */
  quoteAsset: Address,
  quoteSymbol: z.string().nullable(),
  quoteDecimals: z.number().int().min(0).max(36).nullable(),
  tokenIn: Address,
  tokenOut: Address,
  amountIn: WeiString,
  amountOut: WeiString,
  /** `amountOut` less `slippageBps`: the swap reverts below it. */
  minOut: WeiString,
  slippageBps: z.number().int().min(0).max(5000),
  /** Shortfall against a 1/1000-size probe. */
  priceImpactBps: z.number().int().nonnegative(),
  /** Average execution price, quote units per 1 agent token; null when decimals are unknown. */
  priceInQuote: DecimalString.nullable(),
  poolId: Hash32,
  /** Pool LP fee in hundredths of a bip (10 000 = 1 %). */
  lpFee: z.number().int().nonnegative(),
  /** UniversalRouter the swap goes through. */
  router: Address,
  /** `exact`: V4Quoter at `blockNumber`; `simulated`: only from test doubles (ADR-0008). */
  exactness: z.enum(['exact', 'simulated']),
  blockNumber: WeiString.nullable(),
  quotedAt: IsoTimestamp,
})
export type TokenTradeQuote = z.output<typeof TokenTradeQuote>

export const TokenSwapBody = z.object({
  side: TokenTradeSide,
  amount: WeiString.refine((v) => v !== '0', 'amount must be > 0'),
  slippageBps: z.number().int().min(0).max(5000).default(100),
})
export type TokenSwapBody = z.input<typeof TokenSwapBody>

/** One wallet transaction of the swap sequence: ERC-20 approve to Permit2, Permit2 allowance, swap. */
export const TokenSwapStep = z.object({
  kind: z.enum(['approve', 'permit2', 'swap']),
  label: z.string(),
  tx: UnsignedTx,
})
export type TokenSwapStep = z.output<typeof TokenSwapStep>

export const TokenSwapResult = z.object({
  quote: TokenTradeQuote,
  /** The wallet that must sign (the caller's primary wallet); output goes to it. */
  owner: Address,
  steps: z.array(TokenSwapStep),
  /** `steps[].tx` in order. */
  txs: z.array(UnsignedTx),
  /** Swap deadline (UniversalRouter `execute`). */
  deadline: IsoTimestamp,
  /** Always false in the product (there is no mock chain, ADR-0008); kept for wire compatibility. */
  simulated: z.boolean(),
})
export type TokenSwapResult = z.output<typeof TokenSwapResult>
